import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { StatusIcon } from "../components/StatusIcon";
import { sourceStatus } from "../lib/sourceStatus";


type Source = {
  id: number; name: string; code: string; url: string; frequency: string;
  is_active: boolean; last_checked_at: string | null; last_successful_at: string | null; monitoring_since?: string | null;
  cron_expression: string; disabled_reason?: string | null;
  /** Anexo II-A 4.9: última detección de cambios y próxima ejecución prevista. La
   *  próxima la calcula el backend con el calendario REAL de Celery (antes se
   *  estimaba aquí con `cron_expression`, que el planificador ignora). */
  last_change_at?: string | null; next_run_at?: string | null;
  /** Fuente con frecuencia mínima pactada: las del contrato con Italfarmaco (catálogo 4.5). Se enseña como
   *  «frecuencia contractual» (Carlos, 23 oct 2026: el contrato habla de frecuencias mínimas de comprobación). */
  contrato?: { n: number; nombre: string; frecuencia_pactada: string }[];
  /** Comprobación a demanda (app/on_demand.py): solo viene si se ofrece a este usuario en esta fuente. */
  on_demand?: OnDemand | null;
};

type OnDemandLast = {
  state: "queued" | "running" | "done" | "failed";
  requested_at: string; finished_at: string | null; changes: number | null;
};
type OnDemand = {
  available: boolean; reason: string | null; remaining: number; cap: number; heavy: boolean;
  last: OnDemandLast | null;
};
const OD_REASONS = ["pasada", "mantenimiento", "en_curso", "leyendo", "tope", "cola_no_disponible"];

/** Las once fuentes del contrato; `pendiente` = aún sin fuente activa en HelixScan. */
type ContractRow = { n: number; nombre: string; frecuencia_pactada: string; sondeo: string | null; pendiente: boolean };
/** Fuentes en estudio (catálogo 4.5, apartado 5.4: el MRI). Su estado y desde cuándo es contractual. */
type StudyRow = { apartado: string; nombre: string; organismo: string; estado: "operativa" | "no_disponible"; desde: string | null };


// sourceStatus + OK_HOURS extraídos a lib/sourceStatus.ts (testeable).

// v3 liquid glass: el estado = punto de color semántico + etiqueta neutra.
const STATUS_CFG = {
  ok:       { labelKey: "sources.statusOk",       dot: "#22c55e" },
  warning:  { labelKey: "sources.statusWarning",  dot: "#f59e0b" },
  error:    { labelKey: "sources.statusError",    dot: "#f87171" },
  pending:  { labelKey: "sources.statusPending",  dot: "#94a3b8" },
  inactive: { labelKey: "sources.statusInactive", dot: "#64748b" },
};

export default function SourcesPage() {
  const t = useT();
  const fmt = useFmt();
  const [sources, setSources] = useState<Source[]>([]);
  const [triggering, setTriggering] = useState<number | null>(null);
  const [msg, setMsg] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const role = useAuthStore(s => s.user?.role);
  const [onlyContract, setOnlyContract] = useState(false);
  const [contract, setContract] = useState<ContractRow[]>([]);
  const [study, setStudy] = useState<StudyRow[]>([]);

  useEffect(() => {
    setLoadError(false);
    api.get<Source[]>("/sources").then(setSources).catch(() => setLoadError(true));
    api.get<ContractRow[]>("/sources/contract").then(setContract).catch(() => setContract([]));
    api.get<StudyRow[]>("/sources/study").then(setStudy).catch(() => setStudy([]));
  }, [retryKey]);

  // Mientras haya una comprobación a demanda en cola o en curso, refrescar la lista para ver su resultado.
  const checking = sources.some(s => s.on_demand?.last && ["queued", "running"].includes(s.on_demand.last.state));
  useEffect(() => {
    if (!checking) return;
    const id = setInterval(() => { api.get<Source[]>("/sources").then(setSources).catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, [checking]);

  const odReason = (reason: string | null, cap: number) =>
    t(reason && OD_REASONS.includes(reason) ? `sources.odReason_${reason}` : "sources.odReason_other", { cap });

  const check = async (s: Source) => {
    setTriggering(s.id);
    try {
      const od = await api.post<OnDemand>(`/sources/${s.id}/check`, {});
      setSources(prev => prev.map(x => (x.id === s.id ? { ...x, on_demand: od } : x)));
      setMsg(t("sources.odQueuedMsg", { name: s.name }));
    } catch (e: any) {
      setMsg(t("sources.errorPrefix") + odReason(e.message, s.on_demand?.cap ?? 3));
      api.get<Source[]>("/sources").then(setSources).catch(() => {});
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const odStatus = (s: Source): { text: string; color: string } | null => {
    const last = s.on_demand?.last;
    if (!last) return null;
    const time = fmt.dateTime(new Date(last.finished_at ?? last.requested_at), { hour: "2-digit", minute: "2-digit" });
    switch (last.state) {
      case "queued": return { text: t(s.on_demand!.heavy ? "sources.odQueuedHeavy" : "sources.odQueued"), color: "var(--clr-text3)" };
      case "running": return { text: t("sources.odRunning"), color: "var(--clr-text3)" };
      case "failed": return { text: t("sources.odFailed", { time }), color: "#d97706" };
      default: {
        const n = last.changes ?? 0;
        const key = n === 0 ? "sources.odDoneNone" : n === 1 ? "sources.odDoneOne" : "sources.odDoneSome";
        return { text: t(key, { time, n }), color: n > 0 ? "var(--clr-org-primary)" : "var(--clr-text3)" };
      }
    }
  };
  const onDemandCap = sources.find(s => s.on_demand)?.on_demand?.cap;

  const trigger = async (id: number, name: string) => {
    setTriggering(id);
    try {
      await api.post(`/sources/${id}/trigger`, {});
      setMsg(t("sources.queued", { name }));
    } catch (e: any) {
      setMsg(t("sources.errorPrefix") + e.message);
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  // Ordenar: activas primero (ok > warning > error > pending), inactivas al final
  const ORDER = { ok: 0, warning: 1, error: 2, pending: 3, inactive: 4 };
  const sorted = [...sources].filter(s => !onlyContract || (s.contrato?.length ?? 0) > 0).sort((a, b) => {
    const sa = sourceStatus(a), sb = sourceStatus(b);
    return (ORDER[sa] ?? 5) - (ORDER[sb] ?? 5);
  });
  const pendingContract = onlyContract ? contract.filter(c => c.pendiente) : [];
  const chip = (on: boolean): React.CSSProperties => ({
    background: on ? "var(--clr-org-primary)" : "var(--clr-surface)", color: on ? "#fff" : "var(--clr-text2)",
    border: on ? "1px solid transparent" : "1px solid var(--clr-border)", borderRadius: 999,
    padding: "5px 14px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
  });

  // Estado del servicio de cara al cliente: semáforo + fuentes al día. Sin números
  // de SLA comprometidos (esos van al contrato) — solo salud real.
  const activeSources = sources.filter(s => sourceStatus(s) !== "inactive");
  const freshCount = activeSources.filter(s => sourceStatus(s) === "ok").length;
  const serviceOk = activeSources.length > 0 && freshCount === activeSources.length;
  const serviceColor = serviceOk ? "#16a34a" : "#d97706";

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("sources.title")}</h1>
        {msg && (
          <span style={{ color: msg.startsWith(t("sources.errorPrefix")) ? "#f87171" : "#22c55e", fontSize: "0.82rem" }}>
            {msg}
          </span>
        )}
      </div>

      {/* Estado del servicio (cliente): salud consolidada, sin SLA comprometido */}
      {sources.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          background: serviceColor + "14", border: `1px solid ${serviceColor}44`,
          borderRadius: 16, padding: "0.85rem 1.15rem", marginBottom: 16,
          backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        }}>
          <StatusIcon status={serviceOk ? "ok" : "warning"} size={14} color={serviceColor} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--clr-text)" }}>
              {serviceOk ? t("sources.serviceOk") : t("sources.serviceIncident")}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)", marginTop: 2 }}>
              {t("sources.serviceSubtitle", { total: activeSources.length, fresh: freshCount })}
            </div>
          </div>
        </div>
      )}

      {/* Fuentes del contrato con Italfarmaco (catálogo 4.5, hoja «Fuentes contractuales») */}
      {contract.length > 0 && (
        <div role="group" aria-label={t("sources.contractFilter")} style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button onClick={() => setOnlyContract(false)} aria-pressed={!onlyContract} style={chip(!onlyContract)}>{t("sources.filterAll")}</button>
          <button onClick={() => setOnlyContract(true)} aria-pressed={onlyContract} style={chip(onlyContract)}>{t("sources.filterContract", { n: contract.length })}</button>
        </div>
      )}

      {/* Comprobación a demanda: qué hace y qué no (propuesta de apartado 4.11 del Anexo II-A) */}
      {onDemandCap !== undefined && (
        <p style={{ fontSize: "0.76rem", color: "var(--clr-text3)", margin: "0 0 12px", lineHeight: 1.5, maxWidth: 820 }}>
          {t("sources.odHint", { cap: onDemandCap })}
        </p>
      )}

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "12px 16px", marginBottom: 16, flexWrap: "wrap", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: "#ef4444", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: "0.85rem", color: "var(--clr-text2)", minWidth: 180 }}>{t("sources.loadError")}</span>
          <button onClick={() => setRetryKey(k => k + 1)} style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "8px 18px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>{t("sources.retry")}</button>
        </div>
      )}

      <div style={{
        background: "var(--clr-surface)",
        border: "1px solid var(--clr-border)",
        borderRadius: 18,
        overflow: "hidden",
        backdropFilter: "var(--clr-blur)",
        WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
      }}>
        {sorted.map((s, idx) => {
          const st = sourceStatus(s);
          const cfg = STATUS_CFG[st];
          const inactive = st === "inactive";
          const disabledReason = inactive
            ? s.disabled_reason || t("sources.disabledDefault")
            : null;

          return (
            <div
              key={s.id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 14,
                padding: "1rem 1.4rem",
                borderBottom: idx < sorted.length - 1 ? "1px solid var(--clr-border2)" : "none",
                opacity: inactive ? 0.55 : 1,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => { if (!inactive) (e.currentTarget as HTMLElement).style.background = "var(--clr-hover)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ""; }}
            >
              {/* Dot */}
              <div style={{
                width: 8, height: 8, borderRadius: 4,
                background: cfg.dot,
                flexShrink: 0,
              }} />

              {/* Info (en móvil, el botón y el estado bajan a su propia línea) */}
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: inactive ? "var(--clr-text3)" : "var(--clr-text)",
                  marginBottom: 3,
                }}>
                  {s.name}
                  {(s.contrato?.length ?? 0) > 0 && (
                    <span title={s.contrato!.map(c => c.nombre).join("\n")} style={{
                      marginLeft: 8, display: "inline-block", verticalAlign: "1px", fontSize: "0.66rem", fontWeight: 600,
                      color: "var(--clr-org-primary)", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "1px 8px",
                    }}>
                      {/* Si cubre varias (CIMA: nº 1 y 4), la del nº más bajo, que es la más exigente */}
                      {t("sources.contractBadge", { freq: s.contrato![0].frecuencia_pactada })}
                    </span>
                  )}
                </div>

                {inactive ? (
                  <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                    {disabledReason}
                  </div>
                ) : (
                  <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {/* Desde cuándo la vigila HelixScan (Carlos, 20 oct 2026) */}
                    {s.monitoring_since && (
                      <span>
                        <span>{t("sources.monitoredSince")}</span>
                        <span style={{ color: "var(--clr-text3)", fontWeight: 600 }}>{fmt.date(new Date(s.monitoring_since))}</span>
                      </span>
                    )}
                    {([
                      ["sources.last", s.last_checked_at],
                      ["sources.lastChange", s.last_change_at],
                      ["sources.next", s.next_run_at],
                    ] as const).map(([label, iso]) => (
                      <span key={label}>
                        <span style={{ color: "var(--clr-text4)" }}>{t(label)}</span>
                        <span style={{ color: "var(--clr-text3)" }}>
                          {iso
                            ? fmt.dateTime(new Date(iso), {
                                day: "numeric", month: "numeric", year: "2-digit",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
                {(() => {
                  const od = odStatus(s);
                  return od && !inactive ? (
                    <div role="status" style={{ fontSize: "0.72rem", color: od.color, fontWeight: 600, marginTop: 4 }}>{od.text}</div>
                  ) : null;
                })()}
              </div>

              {/* Status badge — cápsula tintada */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginLeft: "auto" }}>
                {s.on_demand && !inactive && (() => {
                  const od = s.on_demand;
                  const off = !od.available || triggering === s.id;
                  return (
                    <button
                      onClick={() => check(s)}
                      disabled={off}
                      aria-label={t("sources.odButtonOf", { name: s.name })}
                      title={od.available ? t("sources.odRemaining", { n: od.remaining }) : odReason(od.reason, od.cap)}
                      style={{
                        background: "var(--clr-surface)", color: off ? "var(--clr-text4)" : "var(--clr-org-primary)",
                        border: "1px solid var(--clr-border)", borderRadius: 999, padding: "4px 12px",
                        fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                        cursor: off ? "not-allowed" : "pointer", opacity: off ? 0.6 : 1,
                      }}
                    >
                      {triggering === s.id ? "…" : t("sources.odButton")}
                    </button>
                  );
                })()}
                {role === "superadmin" && !inactive && (
                  <button
                    onClick={() => trigger(s.id, s.name)}
                    disabled={triggering === s.id}
                    aria-label={t("sources.runScrapeOf", { name: s.name })}
                    title={t("sources.runNow")}
                    style={{
                      background: "var(--clr-surface)",
                      color: "var(--clr-text3)",
                      border: "1px solid var(--clr-border)",
                      borderRadius: 999,
                      width: 28, height: 28,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: triggering === s.id ? "not-allowed" : "pointer",
                      opacity: triggering === s.id ? 0.4 : 1,
                      flexShrink: 0,
                    }}
                  >
                    {triggering === s.id ? (
                      <span style={{ fontSize: "0.7rem" }}>…</span>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 4l14 8-14 8V4z" />
                      </svg>
                    )}
                  </button>
                )}
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: cfg.dot + "1e", color: cfg.dot,
                  borderRadius: 999, padding: "3px 11px",
                  fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
                }}>
                  <StatusIcon status={st} size={9} color={cfg.dot} />
                  {t(cfg.labelKey)}
                </span>
              </div>
            </div>
          );
        })}

        {pendingContract.map(c => (
          <div key={`c${c.n}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "1rem 1.4rem", borderTop: "1px solid var(--clr-border2)", opacity: 0.75 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: "#94a3b8", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 3 }}>{c.nombre}</div>
              <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                {t("sources.contractBadge", { freq: c.frecuencia_pactada })} · {t("sources.contractPending")}
              </div>
            </div>
          </div>
        ))}

        {/* Fuentes en estudio (MRI): obligación de medios; estado y fecha visibles por contrato */}
        {sources.length > 0 && study.map(r => {
          const ok = r.estado === "operativa";
          const dot = ok ? "#22c55e" : "#94a3b8";
          const date = r.desde ? fmt.date(new Date(r.desde + "T12:00:00")) : "—";
          return (
            <div key={`e${r.apartado}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "1rem 1.4rem", borderTop: "1px solid var(--clr-border2)" }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: dot, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 3 }}>
                  {r.nombre} ({r.organismo})
                  <span style={{ marginLeft: 8, display: "inline-block", verticalAlign: "1px", fontSize: "0.66rem", fontWeight: 600,
                    color: "var(--clr-text3)", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "1px 8px" }}>
                    {t("sources.studyBadge")}
                  </span>
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)", fontWeight: 600 }}>
                  {ok ? t("sources.studyOperativa", { date }) : t("sources.studyNoDisponible", { date })}
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: 2 }}>{t("sources.studyHint")}</div>
              </div>
            </div>
          );
        })}

        {sources.length === 0 && (
          <div style={{ padding: 40, color: "var(--clr-text4)", textAlign: "center" }}>
            {t("sources.loading")}
          </div>
        )}
      </div>

      {/* Leyenda — cápsulas tintadas */}
      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        {Object.entries(STATUS_CFG).map(([key, cfg]) => (
          <span key={key} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: cfg.dot + "1e", color: cfg.dot,
            borderRadius: 999, padding: "3px 11px",
            fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: cfg.dot, flexShrink: 0 }} />
            {t(cfg.labelKey)}
          </span>
        ))}
      </div>
    </div>
  );
}
